import { StatusModal } from '@/components/StatusModal';
import { PosunoutTerminModal } from '@/components/vyroba/PosunoutTerminModal';
import { TabidooDetailModal } from '@/components/vyroba/TabidooDetailModal';
import type { DilnaProduct, ProductionTerm } from '@/components/vyroba/types';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/** Velikost čekající na posun jednotlivých kusů na další termín. */
type VelikostPending = {
  product: DilnaProduct;
  variantName: string | null;
  label: string;
  max: number;
};

/** Barvy stavů zakázky — stejné odstíny jako v tabulce Výroba. */
const STAV_CLASSES: Record<string, string> = {
  Rozpracováno: 'text-danger bg-danger-light',
  'Připraveno k přidělení': 'text-primary bg-primary-50',
  'Přiděleno švadlence': 'text-primary bg-primary-50',
  'Šije se': 'text-warning bg-warning-light',
  Došito: 'text-success bg-success-light',
  Hotovo: 'text-success bg-success-light',
};

const WEEKDAYS = ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'];

/** Sekundy → „0 h 11 min". */
function fmtSewing(sec: number): string {
  const min = Math.round(sec / 60);
  return `${Math.floor(min / 60)} h ${min % 60} min`;
}

/** 'YYYY-MM-DD' → 'Pondělí' (nad datum v tabu). */
function fmtWeekday(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return '';
  return WEEKDAYS[new Date(Number(y), Number(m) - 1, Number(d)).getDay()] ?? '';
}

/** 'YYYY-MM-DD' → 'D.M.' (krátce, pro taby). */
function fmtShort(iso: string): string {
  const [, m, d] = iso.split('-');
  return d && m ? `${Number.parseInt(d, 10)}.${Number.parseInt(m, 10)}.` : iso;
}

/** 'YYYY-MM-DD' → 'Pondělí 27.7.2026'. */
function fmtFull(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  const wd = WEEKDAYS[new Date(Number(y), Number(m) - 1, Number(d)).getDay()] ?? '';
  return `${wd} ${Number(d)}.${Number(m)}.${y}`;
}

const linkBtn =
  'text-[17px] font-bold text-text-secondary border border-border rounded-lg px-6 py-3 hover:bg-surface-hover transition-colors shrink-0';

/**
 * Dokončovací dílna — plán k termínům výroby. Stejný pohled jako plán stříhání,
 * ale 1:1 s předskladněním: kusy se odečítají až naskladněním z výroby, takže
 * rozpracované zakázky se jen vypisují (ať kolegyně pozná, že se produkt už šije).
 */
export function DokoncovaciDilnaPage() {
  const [terms, setTerms] = useState<ProductionTerm[]>([]);
  // Kolik toho na termín zbývá — podle toho se prázdné taby schovají.
  const [naplnenost, setNaplnenost] = useState<Record<string, number>>({});
  const [activeTerm, setActiveTerm] = useState<ProductionTerm | null>(null);
  const [products, setProducts] = useState<DilnaProduct[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Karty, u kterých je zapnuté zobrazení prodaných kusů.
  const [prodaneOpen, setProdaneOpen] = useState<Record<string, boolean>>({});
  // Produkt čekající na potvrzení „Posunout termín neprodaných kusů".
  const [pending, setPending] = useState<DilnaProduct | null>(null);
  // Karty, u kterých zrovna běží posun termínu (blokace tlačítka).
  const [moving, setMoving] = useState<Record<string, boolean>>({});
  // Velikost čekající na potvrzení posunu (počet kusů zvolí kolegyně v modalu).
  const [velPending, setVelPending] = useState<VelikostPending | null>(null);
  const [velQty, setVelQty] = useState(1);
  const [velMoving, setVelMoving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);
  // Zakázka otevřená v detailu (stejný modal jako ve VÝROBA → Databáze výroby).
  const [detailId, setDetailId] = useState<string | null>(null);

  const today = new Date().toISOString().slice(0, 10);
  // Termíny řazené podle data — i ty PO termínu, kde ještě něco zbývá.
  // Zaostávající resty se nesmí tiše ztratit z dohledu.
  const seRazene = [...terms].sort((a, b) => a.term_date.localeCompare(b.term_date));
  // Termíny bez zůstatku se v záložkách vůbec neukazují.
  const tabs = seRazene.filter(
    (t) => (naplnenost[t.term_date] ?? 0) > 0 || activeTerm?.id === t.id,
  );

  // Termíny → taby. Vybere se nejstarší, na kterém ještě něco je (klidně
  // i po termínu), jinak nejbližší budoucí.
  useEffect(() => {
    Promise.all([
      apiFetch<{ terms: ProductionTerm[] }>('/api/vyroba/terms'),
      apiFetch<{ terms: { term_date: string; qty: number }[] }>(
        '/api/vyroba/dokoncovaci-dilna/terminy',
      ).catch(() => ({ terms: [] })),
    ])
      .then(([d, prehled]) => {
        setTerms(d.terms);
        const obsazeno: Record<string, number> = {};
        for (const p of prehled.terms) obsazeno[p.term_date] = p.qty;
        setNaplnenost(obsazeno);
        const t = new Date().toISOString().slice(0, 10);
        const serazene = [...d.terms].sort((a, b) => a.term_date.localeCompare(b.term_date));
        const vybrany =
          serazene.find((x) => (obsazeno[x.term_date] ?? 0) > 0) ??
          serazene.find((x) => x.term_date >= t) ??
          serazene[0] ??
          null;
        if (vybrany) setActiveTerm(vybrany);
      })
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : 'Chyba načítání termínů'),
      );
  }, []);

  // Kvůli rychlému přeskakování mezi taby — donačtená "prodáno" data se smí
  // sloučit jen do produktů termínu, na který se pořád ještě díváme.
  const activeTermRef = useRef<ProductionTerm | null>(null);
  useEffect(() => {
    activeTermRef.current = activeTerm;
  }, [activeTerm]);

  /**
   * Prodané kusy — samostatný, pomalejší dotaz (živý Shoptet nárok na každé
   * SKU). Karty se vykreslí bez čekání na tohle; čísla "prodáno" se sloučí
   * do stavu, jakmile dorazí.
   */
  const loadSold = useCallback(async (term: ProductionTerm) => {
    try {
      const d = await apiFetch<{ sold: Record<string, number> }>(
        `/api/vyroba/dokoncovaci-dilna/prodano?term_date=${term.term_date}`,
      );
      if (activeTermRef.current?.term_date !== term.term_date) return;
      setProducts((prev) =>
        prev
          ? prev.map((p) => ({
              ...p,
              variants: p.variants.map((v) => ({
                ...v,
                sold: d.sold[`${p.product_code}|${v.variant_name ?? ''}`] ?? 0,
              })),
            }))
          : prev,
      );
    } catch {
      // Tichá chyba — karty zůstanou, jen "prodáno" zůstane nedonačtené.
    }
  }, []);

  /**
   * Produkty vybraného termínu. `silent` = po akci (posun termínu/velikosti)
   * — dotáhne data na pozadí bez zablikání "Načítám…" a bez zbourání gridu.
   */
  const load = useCallback(
    async (term: ProductionTerm, opts?: { silent?: boolean }) => {
      if (!opts?.silent) setProducts(null);
      try {
        const d = await apiFetch<{ products: DilnaProduct[] }>(
          `/api/vyroba/dokoncovaci-dilna?term_date=${term.term_date}`,
        );
        setProducts(
          d.products.map((p) => ({
            ...p,
            variants: p.variants.map((v) => ({ ...v, sold: undefined })),
          })),
        );
      } catch (e: unknown) {
        if (!opts?.silent) setProducts([]);
        setError(e instanceof ApiError ? e.message : 'Chyba načítání produktů');
        return;
      }
      void loadSold(term);
    },
    [loadSold],
  );

  useEffect(() => {
    if (activeTerm) load(activeTerm);
  }, [activeTerm, load]);

  /** Posune neprodané kusy produktu na zvolený termín výroby. */
  async function posunoutTermin(product: DilnaProduct, toTerm: string) {
    setPending(null);
    const term = activeTerm;
    if (!term) return;
    setMoving((prev) => ({ ...prev, [product.product_code]: true }));
    try {
      const res = await apiFetch<{
        term_date: string;
        posunuto: { variant_name: string | null; qty: number }[];
      }>('/api/vyroba/dokoncovaci-dilna/posunout-termin', {
        method: 'POST',
        body: { product_code: product.product_code, term_date: term.term_date, to_term: toTerm },
      });
      const kusy = res.posunuto.reduce((sum, o) => sum + o.qty, 0);
      setToast({
        msg: `${product.product_code}: posunuto ${kusy} ks na ${fmtShort(res.term_date)}`,
        kind: 'success',
      });
      await load(term, { silent: true });
    } catch (e: unknown) {
      setToast({
        msg: e instanceof ApiError ? e.describe() : 'Posun termínu selhal',
        kind: 'error',
      });
    } finally {
      setMoving((prev) => ({ ...prev, [product.product_code]: false }));
    }
  }

  /** Posune zvolený počet kusů jedné velikosti na nejbližší další termín. */
  async function posunoutVelikost() {
    const pending = velPending;
    const term = activeTerm;
    if (!pending || !term) return;
    setVelMoving(true);
    try {
      const res = await apiFetch<{ term_date: string; qty: number }>(
        '/api/vyroba/dokoncovaci-dilna/posunout-velikost',
        {
          method: 'POST',
          body: {
            product_code: pending.product.product_code,
            term_date: term.term_date,
            variant_name: pending.variantName,
            qty: velQty,
          },
        },
      );
      setToast({
        msg: `${pending.product.product_code} ${pending.label}: posunuto ${res.qty} ks na ${fmtShort(res.term_date)}`,
        kind: 'success',
      });
      setVelPending(null);
      await load(term, { silent: true });
    } catch (e: unknown) {
      setToast({
        msg: e instanceof ApiError ? e.describe() : 'Posun velikosti selhal',
        kind: 'error',
      });
    } finally {
      setVelMoving(false);
    }
  }

  return (
    <>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">Dokončovací dílna</h1>
          <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
            Co je k jednotlivým termínům výroby předskladněné. Vyber termín v záložkách.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link
            to="/vyroba/striharna/timeline"
            title="Kdo právě šije a kolik mu zbývá"
            className={linkBtn}
          >
            Timeline
          </Link>
          <Link to="/vyroba/striharna" title="Co je potřeba nastřihat" className={linkBtn}>
            Plán stříhání
          </Link>
        </div>
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      {/* Taby s termíny */}
      {tabs.length > 0 ? (
        <div className="flex flex-wrap gap-2 mb-5">
          {tabs.map((t) => {
            const active = activeTerm?.id === t.id;
            const poTerminu = t.term_date < today;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  // Vymazat rovnou při kliku, ne až v efektu po přepnutí
                  // termínu — ať se skeleton objeví v okamžiku kliknutí.
                  setProducts(null);
                  setActiveTerm(t);
                }}
                // Větší terč na dotyk — hlavní ovládání na tabletu.
                className={`relative rounded-lg px-6 py-3 text-[17px] font-bold transition-colors cursor-pointer ${
                  active
                    ? 'bg-action text-white'
                    : poTerminu
                      ? 'bg-danger-light border border-danger/40 text-danger hover:border-danger'
                      : 'bg-bg border border-border text-text-secondary hover:border-text-muted'
                }`}
              >
                {poTerminu && (
                  <span className="absolute -top-2 -right-2 text-[10px] font-bold uppercase tracking-wide bg-danger text-white rounded px-1.5 py-0.5 whitespace-nowrap">
                    Po termínu
                  </span>
                )}
                <span className="flex flex-col leading-tight">
                  <span
                    className={`text-[14px] font-semibold ${active ? 'text-white/80' : poTerminu ? 'text-danger/80' : 'text-text-muted'}`}
                  >
                    {fmtWeekday(t.term_date)}
                  </span>
                  {fmtShort(t.term_date)}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-text-muted">
          Žádné aktivní termíny výroby. Nejdřív je založ v sekci „Termíny výroby".
        </p>
      )}

      {/* Obsah vybraného termínu */}
      {activeTerm && (
        <>
          <div className="bg-surface rounded-xl shadow-card px-5 py-4 mb-5">
            <h2 className="text-xl font-bold text-text">{fmtFull(activeTerm.term_date)}</h2>
          </div>

          {products == null ? (
            <ProductGridSkeleton />
          ) : products.length === 0 ? (
            <p className="text-sm text-text-muted px-1">
              K tomuto termínu není zatím zařazený žádný produkt.
            </p>
          ) : (
            // Na iPadu (od 768 px) dvě karty vedle sebe, ne jedna přes celou šířku.
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {products.map((p) => (
                <ProductCard
                  key={p.product_code}
                  p={p}
                  prodaneOn={!!prodaneOpen[p.product_code]}
                  onProdane={() =>
                    setProdaneOpen((prev) => ({
                      ...prev,
                      [p.product_code]: !prev[p.product_code],
                    }))
                  }
                  moving={!!moving[p.product_code]}
                  onPosunout={() => setPending(p)}
                  onPosunoutVelikost={(variantName, label, max) => {
                    setVelPending({ product: p, variantName, label, max });
                    setVelQty(Math.max(1, max));
                  }}
                  onOpenOrder={setDetailId}
                />
              ))}
            </div>
          )}
        </>
      )}

      {pending && activeTerm && (
        <PosunoutTerminModal
          productCode={pending.product_code}
          fromTerm={activeTerm.term_date}
          terms={terms}
          onConfirm={(toTerm) => posunoutTermin(pending, toTerm)}
          onCancel={() => setPending(null)}
        />
      )}

      {velPending && (
        <PosunoutVelikostModal
          pending={velPending}
          qty={velQty}
          onQty={setVelQty}
          moving={velMoving}
          onConfirm={posunoutVelikost}
          onCancel={() => setVelPending(null)}
        />
      )}

      {toast && (
        <StatusModal message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />
      )}

      {detailId && (
        <TabidooDetailModal
          table="vyroba"
          recordId={detailId}
          onClose={() => setDetailId(null)}
          onSaved={async () => {
            if (activeTerm) await load(activeTerm, { silent: true });
          }}
        />
      )}
    </>
  );
}

/** Zástupné karty při přepnutí termínu — kopírují rozvržení skutečných karet. */
function ProductGridSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {Array.from({ length: 4 }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: statický počet, nic se nepřeuspořádává
        <div key={i} className="bg-surface rounded-xl shadow-card p-5 animate-pulse">
          <div className="flex gap-5">
            <div className="w-[104px] h-[104px] rounded-lg bg-bg shrink-0" />
            <div className="min-w-0 flex-1 space-y-2.5">
              <div className="h-3.5 w-20 rounded bg-bg" />
              <div className="h-5 w-3/4 rounded bg-bg" />
              <div className="h-3.5 w-1/2 rounded bg-bg" />
              <div className="mt-3 space-y-2">
                <div className="h-4 w-full rounded bg-bg" />
                <div className="h-4 w-full rounded bg-bg" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ProductCard({
  p,
  prodaneOn,
  onProdane,
  moving,
  onPosunout,
  onPosunoutVelikost,
  onOpenOrder,
}: {
  p: DilnaProduct;
  /** Ukazovat u velikostí, kolik kusů je už prodaných. */
  prodaneOn: boolean;
  onProdane: () => void;
  /** Právě běží posun termínu téhle karty. */
  moving: boolean;
  onPosunout: () => void;
  /** Otevře modal pro posun konkrétního počtu kusů jedné velikosti. */
  onPosunoutVelikost: (variantName: string | null, label: string, max: number) => void;
  /** Otevře detail zakázky (stejný modal jako ve VÝROBA). */
  onOpenOrder: (tabidooId: string) => void;
}) {
  return (
    <div className="bg-surface rounded-xl shadow-card p-5">
      {/* Přepínače vpravo nahoře — prodané kusy a posun termínu. */}
      <div className="flex items-center justify-end gap-3 -mt-1 -mr-1 mb-1">
        <button
          type="button"
          onClick={onPosunout}
          disabled={moving}
          className="text-[13px] font-semibold cursor-pointer text-text-muted hover:text-text-secondary transition-colors disabled:opacity-50 disabled:cursor-wait"
        >
          {moving ? 'Posouvám…' : 'Posunout termín neprodaných kusů'}
        </button>
        <button
          type="button"
          onClick={onProdane}
          className={`text-[13px] font-semibold cursor-pointer transition-colors ${
            prodaneOn ? 'text-warning' : 'text-text-muted hover:text-text-secondary'
          }`}
        >
          {prodaneOn ? 'Skrýt prodané' : 'Ukázat jen prodané'}
        </button>
      </div>
      {p.note && (
        <div className="mb-4 rounded-md border-l-4 border-warning bg-warning-light px-4 py-2.5 text-lg text-text whitespace-pre-wrap">
          {p.note}
        </div>
      )}
      <div className="flex gap-5 md:gap-3 lg:gap-5">
        <div className="shrink-0">
          {p.image_url ? (
            <img
              src={p.image_url}
              alt=""
              className="w-[104px] h-[104px] md:w-[88px] md:h-[88px] lg:w-[104px] lg:h-[104px] object-contain rounded-lg border border-border bg-white p-1"
            />
          ) : (
            <div className="w-[104px] h-[104px] md:w-[88px] md:h-[88px] lg:w-[104px] lg:h-[104px] rounded-lg border border-border bg-bg" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <span className="text-[15px] font-mono text-text-muted">{p.product_code}</span>
          <div className="text-lg font-semibold text-text">{p.product_name}</div>
          {p.naplet && (
            <div className="mt-1 text-[15px]">
              <span className="font-semibold text-text">Náplet: </span>
              <span
                className="inline-block rounded-full px-2.5 py-0.5 text-sm font-semibold align-middle"
                style={{ backgroundColor: p.naplet.bg, color: p.naplet.fg }}
              >
                {p.naplet.barva}
              </span>
            </div>
          )}
          {p.sewing_sec > 0 && (
            <div className="text-[15px] text-text">
              <span className="font-semibold">Čas šití: </span>
              {fmtSewing(p.sewing_sec)}
            </div>
          )}
          <div className="mt-2.5">
            {p.variants.map((v) => {
              const label = v.variant_name ?? 'Bez velikosti';
              return (
                <div
                  key={v.variant_name ?? '__none__'}
                  className="flex items-center justify-between border-b border-border-light py-1.5 text-base"
                >
                  <span className="text-text">{label}</span>
                  <span className="flex items-baseline gap-2">
                    {prodaneOn &&
                      (v.sold == null ? (
                        <span className="inline-block h-4 w-20 rounded bg-warning-light animate-pulse" />
                      ) : (
                        <span className="text-[15px] font-semibold text-warning">
                          prodáno {v.sold} ks
                        </span>
                      ))}
                    <span className="font-semibold text-text">{v.qty} ks</span>
                    {v.qty > 0 && (
                      <button
                        type="button"
                        onClick={() => onPosunoutVelikost(v.variant_name, label, v.qty)}
                        title={`Posunout velikost ${label} na další termín`}
                        className="text-text-muted hover:text-action transition-colors cursor-pointer p-0.5 -mr-0.5"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <title>Posunout</title>
                          <path d="M5 12h14M13 6l6 6-6 6" />
                        </svg>
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="mt-2 text-[15px] text-text-muted">Celkem {p.total_qty} ks</div>
        </div>
      </div>
      {p.open_orders.length > 0 && (
        <div className="mt-2">
          {p.open_orders.map((o) => (
            <button
              key={o.tabidoo_id}
              type="button"
              onClick={() => onOpenOrder(o.tabidoo_id)}
              className="flex w-full items-center gap-3 text-[15px] flex-wrap py-0.5 cursor-pointer rounded hover:bg-surface-hover transition-colors text-left"
            >
              <span className="font-semibold text-text w-16 shrink-0">{o.id_zakazky}</span>
              <span className="text-text-secondary w-28 shrink-0">
                {o.svadlenka || 'nepřidělena'}
              </span>
              {o.stav && (
                <span
                  className={`text-sm font-semibold px-2 py-0.5 rounded ${
                    STAV_CLASSES[o.stav] ?? 'text-text-secondary bg-bg'
                  }`}
                >
                  {o.stav}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Modal pro posun zvoleného počtu kusů jedné velikosti na další termín. */
function PosunoutVelikostModal({
  pending,
  qty,
  onQty,
  moving,
  onConfirm,
  onCancel,
}: {
  pending: VelikostPending;
  qty: number;
  onQty: (qty: number) => void;
  moving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEscapeKey(onCancel);
  const clamp = (n: number) => Math.min(pending.max, Math.max(1, n));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onCancel}
        className="absolute inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-sm bg-surface rounded-2xl shadow-modal p-6 reveal">
        <h2 className="text-lg font-bold text-text mb-1">Posunout velikost {pending.label}</h2>
        <p className="text-sm text-text-secondary mb-5">
          <strong>{pending.product.product_code}</strong> — k dispozici je {pending.max} ks. Kolik
          posunout na nejbližší další termín? Posunou se i prodané kusy, dej pak vědět zákazníkovi.
        </p>
        <div className="flex items-center justify-center gap-4 mb-6">
          <button
            type="button"
            onClick={() => onQty(clamp(qty - 1))}
            disabled={qty <= 1}
            className="w-12 h-12 shrink-0 rounded-lg border border-border text-2xl font-bold text-text-secondary hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
          >
            −
          </button>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={pending.max}
            value={qty}
            onChange={(e) => onQty(clamp(Number.parseInt(e.target.value, 10) || 1))}
            className="w-20 text-center text-2xl font-bold text-text border border-border rounded-lg py-2"
          />
          <button
            type="button"
            onClick={() => onQty(clamp(qty + 1))}
            disabled={qty >= pending.max}
            className="w-12 h-12 shrink-0 rounded-lg border border-border text-2xl font-bold text-text-secondary hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
          >
            +
          </button>
        </div>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm font-semibold text-text-secondary hover:text-text px-4 py-2 cursor-pointer"
          >
            Zrušit
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={moving}
            className="text-sm font-bold rounded-lg px-5 py-2 text-white cursor-pointer transition-colors bg-action hover:bg-action-dark disabled:opacity-50 disabled:cursor-wait"
          >
            {moving ? 'Posouvám…' : 'Posunout'}
          </button>
        </div>
      </div>
    </div>
  );
}
